import axios from "axios";
import {url} from "../../crudRequests";

// Patient-side client for the consent endpoints.
//
// The tenant and the acting patient are resolved SERVER-side from the session;
// the only thing sent from here is which patient the consent is for, which the
// server re-checks against the session (the patient themselves, or an active
// guardian). See blhWebServer/src/domain/consents/PATIENT_CONSENT_DESIGN.md.

function authConfig(state, extra = {}) {
  return {
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + (state?.jwt || ""),
      "X-Tenant": state?.db || state?.organization?.db || "",
      ...(extra.headers || {}),
    },
    withCredentials: true,
    ...(extra.responseType ? {responseType: extra.responseType} : {}),
  };
}

export async function fetchConsentStatus(state, pid) {
  const response = await axios.get(`${url}/patient-consents/status`, {
    ...authConfig(state),
    // `scope: "any"` is deliberate and belongs to THIS caller only. The patient
    // is asking "have I signed anything?", not "may this claim go out" — they
    // have no carrier in hand and no claim in front of them. Every claim-side
    // caller must pass an insurerId instead, because a consent authorizes one
    // named policy; asking without one now returns nothing rather than an
    // unrelated consent.
    params: {pid, scope: "any"},
  });
  return response?.data || null;
}

/**
 * The official form, pre-filled by the server with the clinic's and the
 * patient's details. Returned as an object URL so it can be shown in an
 * iframe — the endpoint needs the auth header, so a bare src= would 401.
 */
export async function fetchConsentFormBlobUrl(state, pid, locationId = null) {
  const response = await axios.get(`${url}/patient-consents/blank-form`, {
    ...authConfig(state, {responseType: "blob"}),
    // `flatten` because this is the READING copy shown beside the signing
    // controls. A live AcroForm here would show a second, inert pair of
    // checkboxes that record nothing.
    params: {pid, locationId, flatten: true},
  });
  return window.URL.createObjectURL(
    new Blob([response.data], {type: "application/pdf"})
  );
}

/**
 * Submit a signature.
 *
 * Sends the drawn signature and the two consent decisions — never a PDF. The
 * server fills the official form, stamps the signature, flattens it and stores
 * it, so the document on file is guaranteed to be the real form with
 * server-resolved values.
 */
export async function signConsent(
  state,
  {
    pid,
    locationId = null,
    signatureImage,
    signerName,
    signerRole = "patient",
    personalInfoConsent,
    benefitAssignment,
  }
) {
  const response = await axios.post(
    `${url}/patient-consents/sign`,
    {
      pid,
      locationId,
      signatureImage,
      signerName,
      signerRole,
      personalInfoConsent,
      benefitAssignment,
    },
    authConfig(state)
  );
  return response?.data?.consent || null;
}

/**
 * Send back a copy the patient signed on their own machine.
 *
 * The two decisions still travel as explicit answers rather than being read
 * out of the file: parsing a PDF to guess what somebody ticked is unreliable,
 * and what the clinic needs recorded is what the patient SAYS they agreed to.
 */
export async function uploadSignedConsent(
  params,
  {file, signerName, personalInfoConsent, benefitAssignment}
) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("params", params);
  if (signerName) formData.append("signerName", signerName);
  formData.append("personalInfoConsent", personalInfoConsent ? "true" : "false");
  formData.append("benefitAssignment", benefitAssignment ? "true" : "false");

  const response = await axios.post(`${url}/public/consent/upload`, formData, {
    headers: {"Content-Type": "multipart/form-data"},
  });
  return response?.data || null;
}

export async function fetchConsentDocumentUrl(state, consentId) {
  const response = await axios.get(`${url}/patient-consents/document`, {
    ...authConfig(state),
    params: {consentId},
  });
  return response?.data || null;
}
